import type {ReactElement, ReactNode} from 'react';
import React, {useState} from 'react';
import {motion} from 'framer-motion';
import type {AiPromptBoxProTipProps, ImageProTipProps, TextProTipProps} from './ai-prompt-box-pro-tip.types';
import {ProTipVariant} from './ai-prompt-box-pro-tip.types';
import styles from './ai-prompt-box-pro-tip.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';

const ANIMATION_ENTER_DURATION_S = 0.3;
const ANIMATION_EXIT_DURATION_S = 0.6;

function TextProTipBody({icon, message}: Pick<TextProTipProps, 'icon' | 'message'>): ReactElement {
  return (
    <div className={styles.tipContent}>
      <Icon
        icon={icon}
        size={IconSize.SIZE_ICON_16}
        type={IconType.FLAT}
        className={styles.tipIcon}
      />
      <span className={styles.tipText}>
        {message}
      </span>
    </div>
  );
}

interface VisualProTipBodyProps {
  visual: ReactNode;
  title: string;
  description: string;
}

function VisualProTipBody({visual, title, description}: VisualProTipBodyProps): ReactElement {
  return (
    <div className={styles.imageTipContent}>
      <div className={styles.tipImageSlot}>{visual}</div>
      <div className={styles.imageTextColumn}>
        <span className={styles.tipTitle}>{title}</span>
        <span className={styles.tipDescription}>{description}</span>
      </div>
    </div>
  );
}

function ImageProTipVisual({imageUrl}: Pick<ImageProTipProps, 'imageUrl'>): ReactElement {
  const [isImageSettled, setIsImageSettled] = useState(false);

  const handleSettled = (): void => {
    setIsImageSettled(true);
  };

  return (
    <>
      {isImageSettled ? null : (
        <SkeletonBrick
          theme={SkeletonBrickTheme.AI_GRADIENT}
          className={styles.tipImageSkeleton}
          radius="4px"
        />
      )}
      <img
        src={imageUrl}
        alt=""
        className={styles.tipImage}
        data-loaded={isImageSettled}
        onLoad={handleSettled}
        onError={handleSettled}
      />
    </>
  );
}

function renderBody(props: AiPromptBoxProTipProps): ReactElement {
  if (props.variant === ProTipVariant.IMAGE) {
    return <VisualProTipBody visual={<ImageProTipVisual imageUrl={props.imageUrl} />} title={props.title} description={props.description} />;
  }
  if (props.variant === ProTipVariant.COMPONENT) {
    return <VisualProTipBody visual={<props.VisualComponent />} title={props.title} description={props.description} />;
  }
  return <TextProTipBody icon={props.icon} message={props.message} />;
}

function getContainerClass(variant: ProTipVariant, isMobile: boolean): string {
  if (variant === ProTipVariant.TEXT) {
    return styles.proTipContainer;
  }
  const classes = [styles.proTipContainer, styles.imageVariant];
  if (isMobile) {
    classes.push(styles.imageVariantMobile);
  }
  return classes.join(' ');
}

export function AiPromptBoxProTip(props: AiPromptBoxProTipProps): ReactElement {
  const {type, variant, onDismiss} = props;
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);
  const containerClass = getContainerClass(variant, isMobile);

  return (
    <motion.div
      className={containerClass}
      data-tip-type={type}
      initial={{y: '100%', opacity: 0}}
      animate={{y: 0, opacity: 1, transition: {duration: ANIMATION_ENTER_DURATION_S, ease: 'easeInOut'}}}
      exit={{y: '100%', opacity: 0, transition: {duration: ANIMATION_EXIT_DURATION_S, ease: 'easeInOut'}}}
    >
      <div className={styles.contentRow}>
        {renderBody(props)}
        <Icon
          icon="icon-close"
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST_UNCOLORED}
          onClick={onDismiss}
          className={styles.closeButton}
          isUsedAsButton
          ariaLabel="Dismiss tip"
        />
      </div>
    </motion.div>
  );
}
